import { FC, useEffect, useState } from "react";
import classes from './studentsArchive.module.scss'
import { IStudent, studentService } from "../../../../entities/student";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useMyActions } from "../../../../entities/my";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SearchItems } from "../../../../features/searchItems";
import { useAppSelector } from "../../../../app/store/store";
import { CommentsCount } from "../../../../entities/student/ui/item/CommentsCount";
import idmClasses from "../../../idm/ui/idm/idmWidget.module.scss";
import { FEATURES, hasFeature } from "../../../../entities/my";
import tbankLogo from "../../../../shared/lib/assets/tbank.png";
import alphaLogo from "../../../../shared/lib/assets/alpha.png";
import tochkaLogo from "../../../../shared/lib/assets/tochka.png";
import { LabelBadges } from '../../../../shared/ui/labels';

export const StudentsArchive: FC = () => {

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [students, setStudents] = useState<IStudent[]>([])
    const [studentsCount, setStudentsCount] = useState<number>(0)
    const [studentsSearch, setStudentsSearch] = useState<IStudent[]>([])

    const [params] = useSearchParams()
    const navigate = useNavigate()

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {my} = useAppSelector(s => s.myReducer)

    const getData = async (req: () => Promise<{students: IStudent[], students_count: number}>) => {
        try{
            setIsLoading(true)
            const studentsRes = await req()
            setStudentsCount(studentsRes.students_count)
            setStudents(studentsRes.students)
            setStudentsSearch(studentsRes.students)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении списка студентов', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const onSelectedFilters = () => {
        const isLost = !!params.get('is_lost');
        const workTgIds = params.getAll('work_tg_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id))
        const paymentIds = params.getAll('payment_ids').map(id => parseInt(id, 10))
        const labels = params.getAll('labels')
        const tagIds = params.getAll('tag_ids').map(id => parseInt(id, 10)).filter(id => !isNaN(id))
        getData(() => studentService.getAllByFilters(workTgIds, isLost, true, paymentIds, labels, tagIds))
    }

    useEffect(() => {
        onSelectedFilters()
    }, [params])

    const getPaymentLogo = (paymentName: string) => {
        const normalizedPaymentName = paymentName.trim().toLowerCase()

        if(
            normalizedPaymentName.includes('т-банк')
            || normalizedPaymentName.includes('тбанк')
            || normalizedPaymentName.includes('тиньк')
            || normalizedPaymentName.includes('tbank')
        ){
            return {src: tbankLogo, alt: 'Т-Банк'}
        }

        if(
            normalizedPaymentName.includes('альфа')
            || normalizedPaymentName.includes('alpha')
        ){
            return {src: alphaLogo, alt: 'Альфа'}
        }

        if(
            normalizedPaymentName.includes('точка')
            || normalizedPaymentName.includes('tochka')
        ){
            return {src: tochkaLogo, alt: 'Точка'}
        }

        return null
    }

    return (
        <section className={classes.container}>
            <section className={classes.search}>
                <section className={classes.searchItems}>
                    <SearchItems
                        placeholder="Введите фио студента или родителя"
                        items={students.map(
                            student => ({
                                ...student, 
                                name: student.last_name + ' ' + student.first_name + ' ' + student.middle_name + ' ' + student.parent_full_name
                            })
                        )}
                        setItems={setStudentsSearch}
                    />
                </section>
                {
                    !isLoading
                        &&
                    <section className={classes.studentsCount}>
                        Кол-во студентов: {studentsCount}
                    </section>
                }
            </section>
            {
                isLoading
                    ?
                <section className="loader"><LoaderSpinner /></section>
                    :
                <section className={classes.list}>
                    {studentsSearch.map(student => {
                        const targetUserId = student.user_id
                        const canViewStudentFull = hasFeature(my, FEATURES.STUDENTS.VIEW_FULL, targetUserId)
                        const canOpenStudentCard = hasFeature(my, FEATURES.STUDENTS.CARD_READ, targetUserId)
                        const canReadStudentComments = hasFeature(my, FEATURES.STUDENTS.CARD_COMMENTS_READ, targetUserId)
                        const studentName = `${student.last_name} ${student.first_name} ${student.middle_name}`.trim()
                        const parentFullName = student.parent_full_name?.trim()
                        const tutorName = student.user_name?.trim() || student.tutor_name?.trim()
                        const balance = student.balance?.trim()
                        const paymentName = student.payment_name?.trim()
                        const hasVisibleBalance = Boolean(balance) && balance !== '0'
                        const hasVisiblePayment = Boolean(paymentName) && paymentName !== 'Неизвестная платежка'
                        const paymentLogo = hasVisiblePayment ? getPaymentLogo(paymentName) : null

                        return (
                            <section
                                key={student.id}
                                className={`${idmClasses.staffItem} ${classes.studentCard}`}
                                onClick={canOpenStudentCard ? () => navigate(`/students/${student.id}`) : undefined}
                            >
                                <LabelBadges labels={student.labels} />
                                {canOpenStudentCard
                                    ? <Link className={classes.nameLink} to={`/students/${student.id}`}>
                                        <span className={idmClasses.staffName}>{student.id}. {studentName}</span>
                                    </Link>
                                    : <span className={idmClasses.staffName}>{student.id}. {studentName}</span>}
                                {tutorName && student.user_id && student.user_id > 0 && (
                                    <span className={idmClasses.staffEmail}>
                                        Исполнитель:{' '}
                                        <Link
                                            className={classes.tgLink}
                                            to={`/staff/${student.user_id}`}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {tutorName}
                                        </Link>
                                    </span>
                                )}
                                {
                                    canViewStudentFull
                                        ?
                                    <section className={classes.infoGrid}>
                                        {parentFullName && (
                                            <span className={idmClasses.staffEmail}>
                                                Родитель: {parentFullName}
                                            </span>
                                        )}
                                        {hasVisibleBalance && (
                                            <span className={idmClasses.staffRoles}>
                                                Баланс: {balance} ₽
                                            </span>
                                        )}
                                        {hasVisiblePayment && (
                                            <span className={idmClasses.staffRoles}>
                                                Платежка:
                                                {
                                                    paymentLogo &&
                                                    <img
                                                        src={paymentLogo.src}
                                                        alt={paymentLogo.alt}
                                                        className={classes.paymentLogo}
                                                    />
                                                }
                                                <span className={classes.paymentName}>{paymentName}</span>
                                            </span>
                                        )}
                                        <section className={classes.metaRow}>
                                            {
                                                student.tg?.trim()
                                                    ?
                                                <a
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={classes.tgLink}
                                                    href={student.tg}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    Написать в ТГ
                                                </a>
                                                    :
                                                null
                                            }
                                            {
                                                canReadStudentComments && Boolean(student.comments_count)
                                                    ?
                                                <section className={classes.commentsWrap}>
                                                    <CommentsCount count={student.comments_count} />
                                                </section>
                                                    :
                                                null
                                            }
                                        </section>
                                    </section>
                                        :
                                    <section className={classes.metaRow}>
                                        {
                                            student.tg?.trim()
                                                ?
                                            <a
                                                target="_blank"
                                                rel="noreferrer"
                                                className={classes.tgLink}
                                                href={student.tg}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                Написать в ТГ
                                            </a>
                                                :
                                            null
                                        }
                                    </section>
                                }
                            </section>
                        )
                    })}
                </section>
            }
        </section>
    )
}
